import { Link } from 'react-router';
import { useAuth } from '../../auth/AuthContext';
import { visibleNavItems } from '../../layout/navigation';
import { t, type MessageKey } from '../../i18n';

/** A picture for each area (decorative: the title carries the meaning). */
const ICONS: Record<string, string> = {
  '/notices': '📣',
  '/documents': '🗂️',
  '/certificates': '🎓',
  '/reports': '📊',
  '/my-children': '👨‍👩‍👧',
  '/homework': '📝',
  '/messages': '💬',
  '/my-classes': '🧑‍🏫',
  '/students': '🧒',
  '/attendance': '✅',
  '/library': '📚',
  '/my-library': '📖',
  '/transport': '🚌',
  '/hostel': '🛏️',
  '/inventory': '📦',
  '/hr': '👥',
  '/my-hr': '🧾',
  '/fees': '💳',
  '/accounting': '🧮',
  '/exams': '🏆',
  '/marks': '✍️',
  '/timetable': '🗓️',
  '/my-timetable': '⏰',
  '/leave-requests': '🌴',
  '/academics': '🏫',
  '/admissions': '🚪',
  '/staff': '🪪',
  '/users': '🔑',
  '/schools': '🏛️',
  '/settings': '⚙️',
  '/organization': '🏢',
  '/audit': '🔍',
};

/**
 * The home screen as a set of cards, one for every area the signed-in person may open, each saying what it is
 * for. The same permission rule as the side menu decides what is shown, so a parent sees a handful and an
 * administrator sees everything; the API enforces the same limits regardless.
 */
export function ModuleCards() {
  const { can, canAny } = useAuth();
  const items = visibleNavItems(can, canAny).filter(
    (item) => item.to !== '/' && item.to !== '/inbox',
  );
  if (items.length === 0) return null;
  return (
    <section aria-label={t('home.title')} className="space-y-3">
      <h2 className="text-lg font-semibold">{t('home.title')}</h2>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <li key={`${item.to}:${item.labelKey}`}>
            <Link
              to={item.to}
              className="flex h-full gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-700 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
            >
              <span aria-hidden="true" className="text-2xl leading-none">
                {ICONS[item.to] ?? '▪️'}
              </span>
              <span>
                <span className="block font-semibold text-brand-900">{t(item.labelKey)}</span>
                <span className="mt-0.5 block text-sm text-slate-600">
                  {t(`home.desc.${item.to}` as MessageKey)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
